import React, { useContext, useEffect } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { ContentPlaceholder, Row, Text } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { useUserTagline, NO_POSITION_TEXT } from '@evertel/blue-user'
import { APIDataBlueUser, APIDataRoom } from '@evertel/types'
import { SessionService } from '@evertel/session/SessionService'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { MessageWallContext } from '@evertel/message'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'

interface MessageHeaderProps {
    message: DisplayRoomMessage | DisplayThreadMessage
    user?: APIDataBlueUser
    isGuest?: boolean
    onClickUser?: (userId: number) => void
    className?: string
    variant?: 'list-item' | 'search' | 'reply' | 'reply-forwarded'
}

const MessageHeader: React.FC<MessageHeaderProps> = observer(({
    message,
    user,
    isGuest,
    onClickUser,
    className,
    variant = 'list-item'
}) => {
    const { modelData } = useContext(MessageWallContext)

    const sessionService = useService(SessionService, [])
    const daController = useService(DepartmentsAccessController, [message.ownerId])

    const isRoom = Boolean((message as DisplayRoomMessage).roomId)
    const departmentId = isRoom ? ((modelData as APIDataRoom)?.departmentId ?? sessionService.selectedDepartmentId) : undefined

    useEffect(() => {
        if (user?.id && !user?.isBot) {
            daController.init(message.ownerId)
        }
    }, [message.ownerId, user?.id, user?.isBot])

    const { displayPosition, positionText, departmentName } = useUserTagline({
        departmentsAccess: daController.departmentsAccess,
        contextDepartmentId: isRoom ? departmentId : undefined,
        reportingDepartment: user?.reportingDepartment,
        userId: user?.id,
        isBot: user?.isBot
    })

    const subtext = displayPosition

    if (!message) return null

    const userLoaded = user && (user.firstName !== undefined || user.lastName !== undefined)

    return (
        <>
            <Row valign="center" className="flex-wrap">
                {userLoaded ? (
                    <div style={{ maxWidth: '100%' }}>
                        <button
                            type="button"
                            onClick={() => onClickUser?.(user?.id)}
                            className={classNames(className, 'p-0 m-0 text-truncate d-block')}
                            style={{ fontWeight: 'bold', maxWidth: '100%' }}
                            tabIndex={onClickUser ? 0 : -1}
                            {...(!onClickUser && { 'aria-disabled': 'true' })}
                        >
                            {isGuest &&
                                <span className="visually-hidden"> Guest </span>
                            }
                            {user?.firstName || user?.lastName ?
                                `${user?.firstName || ''} ${user?.lastName || ''}` :
                                user?.email
                            }
                            <span className="visually-hidden">
                                (Open Profile)
                                . {/* Pause for screen readers */}
                                Position: {subtext}.
                                Message published at: {moment(message.publishedDate || message.createdDate).format('H:mm')}
                            </span>
                        </button>
                    </div>
                ) : (
                    <Row valign="center">
                        <ContentPlaceholder width={200} height={18} style={{marginRight: 10}}/>
                        <ContentPlaceholder width={100} height={12} />
                    </Row>
                )}

                {variant !== 'reply-forwarded' &&
                    <div className="d-flex ps-2 flex-nowrap fs-6 text-muted" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        {userLoaded && (
                            user?.isBot ? (
                                <Text
                                    size="smaller"
                                    className=""
                                    aria-hidden={true}
                                >
                                    {displayPosition}
                                </Text>
                            ) : departmentName ? (
                                <>
                                    <span
                                        className="d-block text-truncate"
                                        style={{ flexShrink: 1, minWidth: 0, fontStyle: positionText === NO_POSITION_TEXT ? 'italic' : undefined }}
                                        aria-hidden={true}
                                    >
                                        {positionText}
                                    </span>
                                    <span
                                        className="ms-1"
                                        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                        aria-hidden={true}
                                    >
                                        {`@ ${departmentName}`}
                                    </span>
                                </>
                            ) : (
                                <Text
                                    size="smaller"
                                    className="text-truncate"
                                    style={{ flexShrink: 1, minWidth: 0 }}
                                    italic={displayPosition === NO_POSITION_TEXT}
                                    aria-hidden={true}
                                >
                                    {displayPosition}
                                </Text>
                            )
                        )}
                        {variant !== 'reply' &&
                            <Text
                                size="smaller"
                                className="ps-1"
                                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                aria-hidden={true}
                            >
                                &#183; {moment(message.publishedDate || message.createdDate).format('H:mm')}
                            </Text>
                        }
                    </div>
                }
            </Row>
            {variant === 'reply' &&
                <Text
                    size="smaller"
                    bold
                    aria-hidden={true}
                >
                    {moment(message.publishedDate || message.createdDate).format(
                        moment(message.publishedDate || message.createdDate).isSame(new Date(), 'year')
                            ? 'dddd, MMMM D · HH:mm'
                            : 'dddd, MMMM D, YYYY · HH:mm'
                    )}
                </Text>
            }
        </>
    )
})

export { MessageHeader }
